import React, { useMemo } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  isToday,
  isSameDay,
} from 'date-fns';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Calendar month view with transaction indicators.
 *
 * Props:
 * - selectedMonth: Date
 * - transactions: Array<{ date: Date, type: 'income' | 'expense' | 'transfer', ... }>
 * - selectedDate: Date | null
 * - onSelectDate: (date: Date) => void
 */
const Calendar = ({ selectedMonth, transactions, selectedDate, onSelectDate }) => {
  const { theme, isDark } = useTheme();

  // Build a map of dateKey -> { hasIncome, hasExpense, hasTransfer, transactions }
  const transactionMap = useMemo(() => {
    const map = {};
    transactions.forEach((t) => {
      if (!t.date) return;
      const key = format(t.date, 'yyyy-MM-dd');
      if (!map[key]) {
        map[key] = {
          hasIncome: false,
          hasExpense: false,
          hasTransfer: false,
          transactions: [],
        };
      }
      if (t.type === 'income') {
        map[key].hasIncome = true;
      } else if (t.type === 'expense') {
        map[key].hasExpense = true;
      } else if (t.type === 'transfer') {
        map[key].hasTransfer = true;
      }
      map[key].transactions.push(t);
    });
    return map;
  }, [transactions]);

  const monthStart = startOfMonth(selectedMonth);
  const monthEnd = endOfMonth(selectedMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 }); // Sunday
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const handleDateClick = (day) => {
    onSelectDate(day);
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* Weekday header */}
      <div
        className="grid grid-cols-7 text-center text-xs md:text-sm py-2 rounded-t-lg transition-colors"
        style={{ 
          backgroundColor: isDark ? theme.inputBackground : '#F9FAFB', 
          color: isDark ? theme.textSecondary : '#4B5563',
          border: `1px solid ${theme.divider}`,
          borderBottom: 'none'
        }}
      >
        {weekdayLabels.map((label) => (
          <div key={label} className="font-semibold uppercase tracking-wide">
            <span className="md:hidden">
              {label.charAt(0)}
            </span>
            <span className="hidden md:inline">
              {label}
            </span>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div 
        className="grid grid-cols-7 gap-px rounded-b-lg overflow-hidden transition-colors" 
        style={{ 
          backgroundColor: isDark ? theme.divider : '#E5E7EB',
          border: `1px solid ${theme.divider}`
        }}
      >
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const info = transactionMap[key];
          const inCurrentMonth = isSameMonth(day, selectedMonth);
          const today = isToday(day);
          const isSelected = selectedDate && isSameDay(day, selectedDate);

          const hasIncome = info?.hasIncome;
          const hasExpense = info?.hasExpense;
          const hasTransfer = info?.hasTransfer;

          return (
            <button
              type="button"
              key={key}
              onClick={() => handleDateClick(day)}
              className={[
                'relative p-2 md:p-3 focus:outline-none transition-all duration-150',
                'min-h-[60px] md:min-h-[80px]',
                'cursor-pointer',
                isSelected ? 'ring-2 ring-red-500 z-10' : '',
              ]
                .join(' ')
                .trim()}
              style={{
                backgroundColor: isSelected 
                  ? (isDark ? '#2D1F1F' : '#FEF2F2')
                  : (isDark ? theme.cardBackground : '#FFFFFF'),
                borderRadius: 0,
              }}
            >
              <div
                className={[
                  'flex items-center justify-center',
                  !inCurrentMonth ? 'opacity-30' : '',
                ]
                  .join(' ')
                  .trim()}
              >
                <span
                  className={[
                    'inline-flex items-center justify-center',
                    'w-7 h-7 md:w-8 md:h-8 rounded-full text-sm md:text-base font-medium',
                  ]
                    .join(' ')
                    .trim()}
                  style={{
                    backgroundColor: today ? (isDark ? '#3D1F1F' : '#FEE2E2') : 'transparent',
                    color: today ? theme.accent : (!inCurrentMonth ? theme.textTertiary : theme.text),
                    fontWeight: today || isSelected ? 600 : 400
                  }}
                >
                  {format(day, 'd')}
                </span>
              </div>

              {/* Dots for income/expense/transfer */}
              {info && (hasIncome || hasExpense || hasTransfer) && (
                <div className="absolute inset-x-0 bottom-2 flex items-center justify-center gap-1">
                  {hasIncome && (
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: theme.accentBlue }} title="Income" />
                  )}
                  {hasExpense && (
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: theme.accent }} title="Expense" />
                  )}
                  {hasTransfer && (
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500" title="Transfer" />
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;
